import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  ShieldCheck,
} from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { useAuth } from "@/contexts/AuthContext";
import { dk } from "./loginDark";

const schema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Enter a valid email"),
  password: z
    .string()
    .min(1, "Password is required")
    .min(8, "At least 8 characters"),
});
type FormValues = z.infer<typeof schema>;

const FieldWrap: React.FC<{
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  delay: string;
}> = ({ icon, label, children, delay }) => (
  <div className="rise" style={{ animationDelay: delay }}>
    <p
      className="mb-2 text-[11px] uppercase tracking-[0.2em]"
      style={{ color: dk.muted }}
    >
      {label}
    </p>
    <div className="input-wrap relative flex items-center">
      <span
        className="input-icon absolute left-4 flex items-center"
        style={{ color: dk.muted }}
      >
        {icon}
      </span>
      {children}
    </div>
  </div>
);

export const LoginForm: React.FC<{ appName: string }> = ({ appName }) => {
  const [apiError, setApiError] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });
  const isLoading = form.formState.isSubmitting;

  const handleSubmit = async (values: FormValues) => {
    setApiError("");
    try {
      await login(values.email.trim(), values.password);
      navigate("/");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Login failed.";
      setApiError(msg || "Please check your credentials and try again.");
    }
  };

  return (
    <section
      className="relative flex items-center justify-center px-6 py-16 sm:px-12"
      style={{ background: dk.panel }}
    >
      {/* subtle top-right glow */}
      <div
        className="pointer-events-none absolute right-0 top-0 h-64 w-64 rounded-full blur-[80px]"
        style={{ background: dk.glow, opacity: 0.6 }}
      />

      <div className="relative z-10 w-full max-w-sm">
        {/* mobile brand */}
        <div className="rise rise-1 mb-8 flex items-center justify-between lg:hidden">
          <span className="fraunces italic text-lg" style={{ color: dk.ink }}>
            {appName}
          </span>
          <ShieldCheck size={18} style={{ color: dk.muted }} />
        </div>

        <h2
          className="fraunces rise rise-2 text-[36px] leading-[1.05] tracking-[-0.01em]"
          style={{ color: dk.ink }}
        >
          Welcome back.
        </h2>
        <p
          className="rise rise-3 mt-2 text-[14px]"
          style={{ color: dk.inkSoft }}
        >
          Enter your admin credentials to continue.
        </p>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="mt-9 space-y-5"
            noValidate
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FieldWrap
                    icon={<Mail size={15} />}
                    label="Email address"
                    delay="320ms"
                  >
                    <FormControl>
                      <input
                        type="email"
                        placeholder="you@example.com"
                        disabled={isLoading}
                        autoComplete="email"
                        autoFocus
                        className="dark-input"
                        style={{ paddingLeft: "2.75rem" }}
                        {...field}
                      />
                    </FormControl>
                  </FieldWrap>
                  <FormMessage
                    className="mt-1 text-[12px]"
                    style={{ color: dk.errorText }}
                  />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FieldWrap
                    icon={<ShieldCheck size={15} />}
                    label="Password"
                    delay="420ms"
                  >
                    <FormControl>
                      <input
                        type={showPwd ? "text" : "password"}
                        placeholder="••••••••"
                        disabled={isLoading}
                        autoComplete="current-password"
                        className="dark-input"
                        style={{
                          paddingLeft: "2.75rem",
                          paddingRight: "2.75rem",
                        }}
                        {...field}
                      />
                    </FormControl>
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-4 flex items-center transition-opacity hover:opacity-100"
                      style={{ color: dk.muted, opacity: 0.7 }}
                    >
                      {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </FieldWrap>
                  <FormMessage
                    className="mt-1 text-[12px]"
                    style={{ color: dk.errorText }}
                  />
                </FormItem>
              )}
            />

            {apiError && (
              <div
                className="rise rounded-lg border-l-2 px-4 py-3 text-[13px]"
                style={{
                  background: dk.errorBg,
                  borderColor: dk.errorBorder,
                  color: dk.errorText,
                }}
              >
                {apiError}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="rise submit-btn group mt-2 flex w-full items-center justify-between rounded-xl px-6 py-4 text-[12px] uppercase tracking-[0.22em] text-white"
              style={{ animationDelay: "520ms" }}
            >
              <span>{isLoading ? "Signing in…" : "Enter the panel"}</span>
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              )}
            </button>

            <p
              className="rise pt-1 text-center text-[11px] uppercase tracking-[0.2em]"
              style={{ color: dk.muted, animationDelay: "600ms" }}
            >
              Admin access only
            </p>
          </form>
        </Form>
      </div>
    </section>
  );
};
